import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import textActions from "actions/text";
import TextingComponents from "../TextingComponents";
import { convertRecipientAccountVariables, convertRecipientContactVariables } from "utils/TextFormUtils";

function TextPanel() {
    const dispatch = useDispatch();
    const textPanelQuery = useSelector(state => state.text.textPanelQuery);
    const { page } = textPanelQuery;
    const textPanelConversationRecipient = useSelector(state => state.text.textPanelConversationRecipient);
    const textPanelConversationSender = useSelector(state => state.text.textPanelConversationSender);
    const recipientId = useSelector(state => state.text.textPanelRecipientId);
    const recipientType = useSelector(state => state.text.textPanelRecipientType);
    const recipientName = useSelector(state => state.text.textPanelRecipientName);
    useEffect(() => {
        const query = { page: 1, limit: 25 };
        dispatch(textActions.setTextPanelQueryParameters(query));
        dispatch(textActions.getTextPanelConversations(query));
        dispatch(textActions.getContactPhones());
    }, [dispatch]);

    const { textPanelConversations = [], textPanelConversations_count = 0, isReadingTextPanelConversations, contactPhones = [] } = useSelector(state => state.text);
    const { filteredMessagesForTextPanel = [], isReadingMessagesForTextPanel } = useSelector(state => state.text);

    const onPageChange = (_, { activePage }) => {
        const updatedQuery = { ...textPanelQuery, page: activePage };
        dispatch(textActions.setTextPanelQueryParameters(updatedQuery));
        dispatch(textActions.getTextPanelConversations(updatedQuery));
    };

    const getConversationBeingViewed = (contact_phone, customer_phone) => {
        return textPanelConversations.find(conversation => conversation.contact_phone === contact_phone && conversation.customer_phone === customer_phone) || {};
    }

    const onRowClick = (conversation) => {
        const { contact_phone, customer_phone } = conversation;
        const conversationBeingViewed = getConversationBeingViewed(contact_phone, customer_phone);
        const recipientType = conversationBeingViewed.recipientInfo?.contact_type;
        const recipientId = conversationBeingViewed.recipientInfo?.id;
        dispatch(textActions.setConversationBeingViewedInTextPanel({ contact_phone, customer_phone, recipientId, recipientType, recipientName: conversationBeingViewed.recipientInfo?.name }));
        dispatch(textActions.getFilteredMessagesForTextPanel({ contact_phone, customer_phone, contact_id:recipientId}));
        if (recipientType) {
            if (recipientType === 'account') {
                dispatch(textActions.getAccountInfo(recipientId));
            } else if (recipientType === 'person') {
                dispatch(textActions.getContactInfo(recipientId));
            }
            dispatch(textActions.getRecipientPhones(recipientId));
        } else {
            dispatch(textActions.clearRecipientPhones());
        }
    };

    const conversationBeingViewed = getConversationBeingViewed(textPanelConversationRecipient, textPanelConversationSender);
    const recipientPhones = useSelector(state => state.text.recipientPhones);
    const isLoadingRecipientDetails = useSelector(state => state.text.isReadingRecipientPhones);
    const userOptions = useSelector(state => state.text.textPanelUserOptions);

    const detailsOfContactRecipient = useSelector(state => state.text.contact);
    const detailsOfAccountRecipient = useSelector(state => state.text.account);
    const isArchivedRecord = conversationBeingViewed?.recipientInfo?.archived;

    return (
        <TextingComponents
            filteredMessages={filteredMessagesForTextPanel}
            isReadingFilteredMessages={isReadingMessagesForTextPanel}
            messages={textPanelConversations}
            messagesCount={textPanelConversations_count}
            loading={isReadingTextPanelConversations}
            onPageChange={onPageChange}
            activePage={page}
            onRowClick={onRowClick}
            recipientId={recipientId}
            recipientName={recipientName || "Unknown"}
            isPanel
            recipientOptions={recipientPhones}
            isLoadingRecipientDetails={isLoadingRecipientDetails}
            selectedRow={conversationBeingViewed.id}
            userOptions={userOptions}
            onReadMessages={(contact_phone, customer_phone) => {
                dispatch(textActions.getFilteredMessagesForTextPanel({ contact_phone, customer_phone }));
                dispatch(textActions.getTextPanelConversations({ page, limit: 25 }));
                dispatch(textActions.setConversationBeingViewedInTextPanel({ contact_phone, customer_phone, recipientName, recipientId, recipientType }));
            }}
            onReplaceRecipientVariable={(variable) => {
                if (recipientType === 'person') {
                    return convertRecipientContactVariables(variable, detailsOfContactRecipient);
                }
                if (recipientType === 'account') {
                    return convertRecipientAccountVariables(variable, detailsOfAccountRecipient);
                }
                return variable;
            }}
            isAccount={recipientType === 'account'}
            contact_phone={textPanelConversationRecipient}
            customer_phone={textPanelConversationSender}
            onChangeNumbers={(payload) => {
                dispatch(textActions.setConversationBeingViewedInTextPanel({ ...payload, recipientId, recipientName, recipientType }));
                dispatch(textActions.getFilteredMessagesForTextPanel(payload));
            }}
            onFilter={(_, { name, value }) => {
                const isSearching = name === 'search' && value;
                const query = isSearching ? { search: value } : { ...textPanelQuery, [name]: value, search: '' };
                dispatch(textActions.setTextPanelQueryParameters(query));
                dispatch(textActions.getTextPanelConversations(isSearching ? { search_phone: value } : { ...textPanelQuery, [name === 'to' ? 'contact_phone' : 'customer_phone']: value }));
            }}
            selectedRecipientFilters={textPanelQuery.to || []}
            selectedSenderFilters={textPanelQuery.from || []}
            recipientFilterOptions={contactPhones.map(phone => ({ phone }))}
            search={textPanelQuery.search || ''}
            isArchivedRecord={isArchivedRecord}
        />
    );
}

export default TextPanel;
